import React, { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

// loadStripe fetches Stripe.js (js.stripe.com) once per publishable key; cache
// the promise so re-opening the dialog doesn't reload the script.
const _stripeCache = {};
function stripeFor(pk) {
  if (!_stripeCache[pk]) _stripeCache[pk] = loadStripe(pk);
  return _stripeCache[pk];
}

function CheckoutForm({ payLabel, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErr("");
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (error) { setErr(error.message || "Payment failed"); setBusy(false); return; }
    if (paymentIntent && ["succeeded", "processing", "requires_capture"].includes(paymentIntent.status)) {
      onSuccess(paymentIntent);
      return;
    }
    setErr("Payment could not be completed. Please try again.");
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="stripe-checkout-form">
      <PaymentElement />
      {err && <div className="text-xs text-[#d41f26] font-semibold" data-testid="stripe-error">{err}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" className="vtx-btn-ghost" onClick={onCancel} disabled={busy}>Back</button>
        <button type="submit" className="vtx-btn-primary" disabled={!stripe || busy} data-testid="stripe-pay">
          {busy ? "Processing…" : payLabel}
        </button>
      </div>
    </form>
  );
}

export default function StripeCheckout({ publishableKey, clientSecret, payLabel = "Pay", onSuccess, onCancel }) {
  const stripePromise = useMemo(() => (publishableKey ? stripeFor(publishableKey) : null), [publishableKey]);
  if (!publishableKey) return <div className="text-xs text-[#d41f26] font-semibold">Stripe publishable key is not configured.</div>;
  if (!clientSecret) return <div className="vtx-skeleton h-24" data-testid="stripe-loading" />;
  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
      <CheckoutForm payLabel={payLabel} onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
}
